// @ts-nocheck
import { useState } from 'react';
import { Button, Card, Code, Group, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import {
  PageTransition,
  PageSlider,
  CrossFade,
  PageReveal,
} from '@/components/animations/page-transitions';

const PAGES = [
  { id: 'home', title: 'Home', body: 'Welcome to the dashboard overview.' },
  { id: 'members', title: 'Members', body: '1,284 active members across 6 ministries.' },
  { id: 'events', title: 'Events', body: '3 upcoming events this week.' },
];

export default function PageTransitionsPage() {
  const [transitionIndex, setTransitionIndex] = useState(0);
  const [sliderIndex, setSliderIndex] = useState(0);
  const [crossfadeIndex, setCrossfadeIndex] = useState(0);
  const [revealKey, setRevealKey] = useState(0);

  const transitionPage = PAGES[transitionIndex];
  const sliderPage = PAGES[sliderIndex];
  const crossfadePage = PAGES[crossfadeIndex];

  const cyclePage = (setter: (fn: (n: number) => number) => void) =>
    setter((n) => (n + 1) % PAGES.length);

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Page Transitions</Title>
        <Text c="dimmed" size="sm">
          Key-driven wrappers that animate full page or view content as it swaps in and out —
          ideal for route changes, wizard steps, and tab content
        </Text>
      </Stack>

      {/* PageTransition */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>PageTransition</Title>
            <Text size="sm" c="dimmed">
              Animates content in/out whenever transitionKey changes, with selectable transition types
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group>
              <Button onClick={() => cyclePage(setTransitionIndex)}>
                Next Page
              </Button>
            </Group>
            <PageTransition transitionKey={transitionPage.id} type="slide-left" duration={350}>
              <Card variant="subtle" p="md">
                <Stack gap={4}>
                  <Text fw={600}>{transitionPage.title}</Text>
                  <Text size="sm" c="dimmed">{transitionPage.body}</Text>
                </Stack>
              </Card>
            </PageTransition>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<PageTransition transitionKey={page.id} type="slide-left" duration={350}>
  <Card variant="subtle" p="md">
    <Text fw={600}>{page.title}</Text>
    <Text size="sm" c="dimmed">{page.body}</Text>
  </Card>
</PageTransition>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • transitionKey - Changing this triggers the exit/enter cycle (required)
              <br />
              • type - 'fade' | 'slide-left' | 'slide-right' | 'slide-up' | 'slide-down' | 'scale' | 'scale-fade' | 'rotate' | 'flip' (default: 'fade')
              <br />
              • duration - Animation duration in ms (default: 300)
              <br />
              • timingFunction - CSS easing function (default: 'ease-out')
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* PageSlider */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>PageSlider</Title>
            <Text size="sm" c="dimmed">
              Slides one page out while the next slides in — useful for wizards, onboarding, and tab flows
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group>
              <Button onClick={() => cyclePage(setSliderIndex)}>
                Next Step
              </Button>
            </Group>
            <PageSlider pageKey={sliderPage.id} direction="left" duration={350}>
              <Card variant="gradient" gradientVariant="primary" p="md">
                <Stack gap={4}>
                  <Text c="white" fw={600}>{sliderPage.title}</Text>
                  <Text c="white" size="sm">{sliderPage.body}</Text>
                </Stack>
              </Card>
            </PageSlider>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<PageSlider pageKey={page.id} direction="left" duration={350}>
  <Card variant="gradient" gradientVariant="primary" p="md">
    <Text c="white" fw={600}>{page.title}</Text>
    <Text c="white" size="sm">{page.body}</Text>
  </Card>
</PageSlider>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • pageKey - Changing this slides the current page out and the next in (required)
              <br />
              • direction - 'left' | 'right' | 'up' | 'down' (default: 'left')
              <br />
              • duration - Animation duration in ms (default: 300)
              <br />
              • timingFunction - CSS easing function (default: cubic-bezier(0.4, 0, 0.2, 1))
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* CrossFade */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>CrossFade</Title>
            <Text size="sm" c="dimmed">
              Fades old content out and new content in simultaneously when transitionKey changes
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group>
              <Button onClick={() => cyclePage(setCrossfadeIndex)}>
                Swap Content
              </Button>
            </Group>
            <CrossFade transitionKey={crossfadePage.id} duration={400}>
              <Card variant="outline" p="md" ta="center">
                <Stack gap={4}>
                  <Text fw={600}>{crossfadePage.title}</Text>
                  <Text size="sm" c="dimmed">{crossfadePage.body}</Text>
                </Stack>
              </Card>
            </CrossFade>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<CrossFade transitionKey={page.id} duration={400}>
  <Card variant="outline" p="md" ta="center">
    <Text fw={600}>{page.title}</Text>
    <Text size="sm" c="dimmed">{page.body}</Text>
  </Card>
</CrossFade>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • transitionKey - Changing this cross-fades to new content (required)
              <br />
              • duration - Animation duration in ms (default: 300)
              <br />
              • timingFunction - CSS easing function (default: 'ease-in-out')
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* PageReveal */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>PageReveal</Title>
            <Text size="sm" c="dimmed">
              Reveals content from behind a colored overlay that sweeps across — great for dramatic entrances
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group>
              <Button onClick={() => setRevealKey((k) => k + 1)}>
                Replay Reveal
              </Button>
            </Group>
            <PageReveal
              key={revealKey}
              direction="left"
              duration={900}
              gradientVariant="primary"
              onRevealComplete={() => {}}
            >
              <Card variant="subtle" p="lg" ta="center">
                <Stack gap={4}>
                  <Text fw={600}>Welcome Aboard</Text>
                  <Text size="sm" c="dimmed">
                    Your member portal is ready.
                  </Text>
                </Stack>
              </Card>
            </PageReveal>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Directions:</Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <PageReveal key={`l-${revealKey}`} direction="top" duration={900}>
                <Card variant="outline" p="md" ta="center">
                  <Text size="sm">Reveal from top</Text>
                </Card>
              </PageReveal>
              <PageReveal key={`c-${revealKey}`} direction="center" duration={900} gradientVariant="secondary">
                <Card variant="outline" p="md" ta="center">
                  <Text size="sm">Reveal from center</Text>
                </Card>
              </PageReveal>
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<PageReveal direction="left" duration={900} gradientVariant="primary">
  <Card variant="subtle" p="lg" ta="center">
    <Text fw={600}>Welcome Aboard</Text>
    <Text size="sm" c="dimmed">Your member portal is ready.</Text>
  </Card>
</PageReveal>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • visible - Whether to run the reveal (default: true)
              <br />
              • direction - 'left' | 'right' | 'top' | 'bottom' | 'center' (default: 'left')
              <br />
              • duration - Total reveal duration in ms (default: 600)
              <br />
              • color - Custom overlay color (overrides gradientVariant)
              <br />
              • gradientVariant - 'primary' | 'secondary' | 'tertiary' (default: 'primary')
              <br />
              • onRevealComplete - Callback fired when the reveal finishes
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Route Changes</Text>
              <Text size="xs" c="dimmed">
                Wrap routed content in PageTransition keyed by the path for smooth navigation between views.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Wizards & Onboarding</Text>
              <Text size="xs" c="dimmed">
                Use PageSlider keyed by the step index so each step slides in from the side.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Tab & Filter Content</Text>
              <Text size="xs" c="dimmed">
                CrossFade keyed by the active tab gives a soft swap without layout jumps.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Hero / Landing Entrances</Text>
              <Text size="xs" c="dimmed">
                PageReveal adds a branded overlay sweep for dramatic first impressions on landing pages.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
